import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import api from "api";
import Util from "utility";
import { __ } from 'locale';
import { openModal, closeModal } from "actions/index";

import PanelRecommendFoods from "./PanelRecommendFoods";
import PanelDecreaseFoods from "./PanelDecreaseFoods";

const helpIndicator = "[&>a]:before:content-['?'] [&>a]:before:absolute [&>a]:before:right-0.5 [&>a]:before:top-0.5 [&>a]:before:flex [&>a]:before:w-4 [&>a]:before:h-4 [&>a]:before:items-center [&>a]:before:justify-center [&>a]:before:text-[9px] [&>a]:before:text-slate-300 [&>a]:before:border [&>a]:before:border-slate-200 [&>a]:before:rounded-full [&>a]:before:z-[1] [&>a:hover]:before:border-green-400 [&>a:hover]:before:text-green-500";

const evalItemSingleCls = `flex items-center text-xs ${helpIndicator} [&>a]:flex [&>a]:items-center [&>a]:w-full [&>a]:py-1 [&>a]:px-2 [&>a]:rounded [&>a]:cursor-pointer [&>a]:relative [&>a>span]:flex-1 [&>a>span]:text-slate-600 [&>a>em]:w-16 [&>a>em]:not-italic [&>a>em]:text-xs [&>a>strong]:font-semibold [&>a>strong]:text-slate-800 [&>a>strong]:ml-auto [&>a:hover]:bg-slate-50`;

const evalItemPairCls = `flex gap-2 mb-3 ${helpIndicator} [&>a]:flex-1 [&>a]:flex [&>a]:flex-col [&>a]:items-center [&>a]:justify-center [&>a]:p-3 [&>a]:rounded-xl [&>a]:bg-slate-50 [&>a]:border [&>a]:border-slate-100 [&>a]:cursor-pointer [&>a]:text-xs [&>a]:text-slate-500 [&>a]:text-center [&>a]:relative [&>a>strong]:block [&>a>strong]:text-xl [&>a>strong]:font-bold [&>a>strong]:text-slate-800 [&>a>strong]:my-0.5 [&>a>p]:m-0 [&>a>p]:text-xs [&>a>p]:mt-1 [&>a:hover]:bg-green-50 [&>a:hover]:border-green-100`;

const evalGroupCls = "mt-1 [&>input]:hidden [&>p]:cursor-pointer [&>p]:m-0 [&>p]:py-1.5 [&>p]:px-2 [&>p]:rounded-lg [&>p]:font-semibold [&>p]:text-xs [&>p]:text-slate-600 [&>p]:uppercase [&>p]:tracking-wide [&>p]:bg-slate-50 [&>p]:mb-0.5 [&>p>label]:cursor-pointer [&>p>label]:flex [&>p>label]:items-center [&>p>label]:gap-1.5 [&>div_b]:font-normal [&>div_strong]:font-normal [&>div]:hidden [&>input:checked~div]:block [&>input:checked+p_label_i]:rotate-90";

const quickResultCls = "text-center text-[11px] m-0 py-2 border-b border-slate-100 leading-tight [&>strong]:block [&>strong]:font-bold [&>strong]:text-slate-800 [&>span]:block [&>span]:py-0.5 [&>span]:px-1 [&>span]:text-slate-400";

const sectionHeader = "text-xs font-bold uppercase tracking-wide text-slate-400 mt-5 mb-2 px-2 pb-1 border-b border-slate-100 first:mt-2";

function EvaluationPanelContents({ results, ddd, wholeDay, quickResults: quickResultsProp, macrosOnly, evaluationCollapsedAtStart, beforeContent, style, className: extraClassName, onRecommendedFoodAdded, standalone }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const { globals } = data;

    const [showAdvancedEvaluation, setShowAdvancedEvaluation] = useState(false);

    const panelTopRef = useRef(0);
    const panelInnerClassRef = useRef('');

    useEffect(() => {
        const el = document.querySelector('.panel-evaluation');
        if (el) panelTopRef.current = el.getBoundingClientRect().top;

        const handleScroll = () => {
            if (!Util.isLectureLikeMode(data.appMode)) return;
            if (window.pageYOffset > panelTopRef.current) {
                if (panelInnerClassRef.current === '') {
                    const inner = document.querySelector('.panel-evaluation-inner');
                    if (inner) inner.classList.add('panel-evaluation-inner-fixed');
                    panelInnerClassRef.current = 'fixed';
                }
            } else {
                if (panelInnerClassRef.current === 'fixed') {
                    const inner = document.querySelector('.panel-evaluation-inner');
                    if (inner) inner.classList.remove('panel-evaluation-inner-fixed');
                    panelInnerClassRef.current = '';
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const showEvaluationGlossary = (id) => {
        if (typeof id !== 'number' || id < 0) return;
        api("glossary/item/" + id).then(result => {
            result = result.data;
            if (result.error) return false;
            dispatch(openModal({
                title: result.title,
                class: 'dialog-flexible',
                content: (
                    <div>
                        <div dangerouslySetInnerHTML={{ __html: result.description }} />
                        <p className="pt-4 mt-4 border-t border-slate-200 text-sm">
                            Více se o nutriční kvalitě výživy dozvíte na{' '}
                            <a className="text-green-600 hover:underline" href="https://www.kurzyatac.cz">www.kurzyatac.cz</a>
                        </p>
                    </div>
                ),
            }));
        });
    };

    const showRecommendedFoods = (e, attr) => {
        e.preventDefault();
        if (e.target !== e.currentTarget) return;
        dispatch(closeModal());
        dispatch(openModal({
            title: "Potraviny vhodné k doplnění: " + globals.attributes[attr],
            closeButton: __('Zavřít'),
            content: (
                <PanelRecommendFoods attr={attr} ddd={ddd[attr]} onFoodAdded={() => { dispatch(closeModal()); if (onRecommendedFoodAdded) onRecommendedFoodAdded(); }} />
            ),
        }));
    };

    const showDecreaseFoods = (e, attr) => {
        e.preventDefault();
        if (e.target !== e.currentTarget) return;
        dispatch(closeModal());
        dispatch(openModal({
            title: "Potraviny s nejvyšším obsahem: " + globals.attributes[attr],
            closeButton: __('Zavřít'),
            content: <PanelDecreaseFoods attr={attr} />,
        }));
    };

    // -------------------------------------------------------------------------
    // Render helpers
    // -------------------------------------------------------------------------

    const renderEnergySection = (res, units, evaluatingWholeDay) => {
        if (evaluatingWholeDay) {
            if (res["energy_balance_kcal"] > 0) {
                let sKcal = res["energy_balance_kcal"].toString();
                let sKj = res["energy_balance_kj"].toString();
                if (sKcal.substr(0, 1) !== "+") sKcal = "+" + sKcal;
                if (sKj.substr(0, 1) !== "+") sKj = "+" + sKj;
                res["energy_balance_kcal"] = sKcal;
                res["energy_balance_kj"] = sKj;
            }
            return (
                <React.Fragment key="energy_balance">
                    <h4 className={sectionHeader}>Energetická bilance</h4>
                    <div className={evalItemPairCls}>
                        <a onClick={() => showEvaluationGlossary(45)}><strong>{res["energy_balance_kcal"]}</strong>{units["energy_balance_kcal"]}</a>
                        <a onClick={() => showEvaluationGlossary(45)}><strong>{res["energy_balance_kj"]}</strong>{units["energy_balance_kj"]}</a>
                    </div>
                    <h4 className={sectionHeader}>Energetický příjem</h4>
                    <div className={evalItemPairCls}>
                        <a onClick={() => showEvaluationGlossary(17)}><strong>{res["energy_kcal"]}</strong>{units["energy_kcal"]}</a>
                        <a onClick={() => showEvaluationGlossary(17)}><strong>{res["energy_kj"]}</strong>{units["energy_kj"]}</a>
                    </div>
                    <h4 className={sectionHeader}>Energetický výdej</h4>
                    <div className={evalItemPairCls}>
                        <a onClick={() => showEvaluationGlossary(54)}><strong>{res["energy_output_kcal"]}</strong>{units["energy_output_kcal"]}</a>
                        <a onClick={() => showEvaluationGlossary(54)}><strong>{res["energy_output_kj"]}</strong>{units["energy_output_kj"]}</a>
                    </div>
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key="energy">
                <h4 className={sectionHeader}>{__('Energie')}</h4>
                <div className={evalItemPairCls}>
                    <a onClick={() => showEvaluationGlossary(17)}><strong>{res["energy_kcal"]}</strong>{units["energy_kcal"]}</a>
                    <a onClick={() => showEvaluationGlossary(17)}><strong>{res["energy_kj"]}</strong>{units["energy_kj"]}</a>
                </div>
            </React.Fragment>
        );
    };

    const renderMacrosBarChart = (res, chartColours) => {
        const barValues = ["proteins", "carbohydrates", "fats", "fiber"];
        const mmax = Math.max(res["proteins"], res["carbohydrates"], res["fats"], res["fiber"]);
        let step = 0.01;
        if (mmax > 1) step = 0.1;
        if (mmax > 10) step = 1;
        if (mmax > 100) step = 10;
        if (mmax > 1000) step = 100;
        if (mmax > 10000) step = 1000;
        const scaleMax = Math.ceil(Math.round(mmax * 1.2) / step) * step;

        const barChart = [];
        for (let i = 0; i < barValues.length; i++) {
            let v = Math.floor((res[barValues[i]] / scaleMax) * 150);
            if (isNaN(v)) continue;
            barChart.push(<rect x={110 + i * 70} y={180 - v} width={65} height={v} fill={chartColours[i]} key={`bc1_${i}`} />);
            barChart.push(<text textAnchor="middle" fontWeight="bold" fontSize="1.3em" x={110 + i * 70 + 32} y={180 - v - 6} fill={chartColours[i]} key={`bc2_${i}`}>{res[barValues[i]]}</text>);
        }

        const chartLabels = [];
        const labStep = scaleMax / 5;
        let currentValue = 0;
        for (let i = 0; i <= 5; i++) {
            const cv = (Math.round(currentValue / step) * step).toPrecision(1);
            chartLabels.push(<text textAnchor="end" fontSize="1.4em" x="72" y={4 + 180 - i * 25} fill="#94a3b8" key={`cl1_${i}`}>{Number(cv)}</text>);
            chartLabels.push(<line x1="80" y1={180 - i * 25} x2="90" y2={180 - i * 25} style={{ stroke: "#cbd5e1" }} key={`cl2_${i}`} />);
            chartLabels.push(<line x1="91" y1={180 - i * 25} x2="390" y2={180 - i * 25} style={{ stroke: "#e2e8f0" }} key={`cl3_${i}`} />);
            currentValue += labStep;
        }

        return (
            <React.Fragment key="macros_wrap">
                <h4 className={sectionHeader}>Makroživiny</h4>
                <div className="px-1 py-1">
                    <svg viewBox="0 0 400 220" width="100%" height="100%">
                        {chartLabels}
                        <line x1="90" y1="30" x2="90" y2="180" style={{ stroke: "#cbd5e1" }} />
                        {barChart}
                        <line x1="90" y1="180" x2="390" y2="180" style={{ stroke: "#cbd5e1" }} />
                        <text fontWeight="bold" textAnchor="middle" fontSize="1.3em" x="140" y="200" fill="#94a3b8">B</text>
                        <text fontWeight="bold" textAnchor="middle" fontSize="1.3em" x="210" y="200" fill="#94a3b8">S</text>
                        <text fontWeight="bold" textAnchor="middle" fontSize="1.3em" x="280" y="200" fill="#94a3b8">T</text>
                        <text fontWeight="bold" textAnchor="middle" fontSize="1.3em" x="350" y="200" fill="#94a3b8">Vl</text>
                    </svg>
                </div>
            </React.Fragment>
        );
    };

    const renderMacrosSubItems = (res, units) => {
        const gk = "Makroživiny";
        const subItems = [];
        for (let subGroup in globals.evaluation_groups[gk]) {
            const attributeKey = globals.evaluation_groups[gk][subGroup];
            const attributeUnit = globals.attribute_units[attributeKey];
            if (typeof res[attributeKey] === "undefined") continue;
            const glossaryId = typeof globals.glossary_ids?.[attributeKey] === 'number' ? globals.glossary_ids[attributeKey] : -1;
            subItems.push(
                <div key={attributeKey} className={evalItemSingleCls}>
                    <a onClick={() => showEvaluationGlossary(glossaryId)}>
                        <span>{globals.attributes[attributeKey]}:</span>
                        <strong>{res[attributeKey]} {attributeUnit}</strong>
                    </a>
                </div>
            );
        }
        if (subItems.length === 0) return false;
        return <React.Fragment key="macros_subitems">{subItems}</React.Fragment>;
    };

    const renderGlycemicSection = (res) => (
        <React.Fragment key="glycemic_info">
            <h4 className={sectionHeader}>Glykémie</h4>
            <div className={evalItemPairCls}>
                <a onClick={() => showEvaluationGlossary(18)}>index<strong>{res["gi"]}</strong></a>
                <a onClick={() => showEvaluationGlossary(19)}>nálož<strong>{res["gn"]}</strong></a>
            </div>
        </React.Fragment>
    );

    const renderSaltCholesterolSection = (res, units) => {
        let riSalt = false, riCholesterol = false;
        let riSaltColor = "#64748b", riCholesterolColor = "#64748b";

        if (ddd && typeof ddd["salt"] !== "undefined" && typeof ddd["cholesterol"] !== "undefined") {
            if (wholeDay === true && (ddd["salt"].overflow_direction === "min" || ddd["salt"].overflow_direction === "max")) riSaltColor = "#ef4444";
            riSalt = <p style={{ margin: "4px 0 0" }}><span style={{ color: riSaltColor, fontSize: '11px' }}>{ddd["salt"].percent} % RI</span></p>;
            if (wholeDay === true && (ddd["cholesterol"].overflow_direction === "min" || ddd["cholesterol"].overflow_direction === "max")) riCholesterolColor = "#ef4444";
            riCholesterol = <p style={{ margin: "4px 0 0" }}><span style={{ color: riCholesterolColor, fontSize: '11px' }}>{ddd["cholesterol"].percent} % RI</span></p>;
        }

        return (
            <React.Fragment key="salt_cholesterol">
                <h4 className={sectionHeader}>Sůl, cholesterol</h4>
                <div className={evalItemPairCls}>
                    <a onClick={() => showEvaluationGlossary(24)}>sůl<strong>{res["salt"]} {units["salt"]}</strong>{riSalt}</a>
                    <a onClick={() => showEvaluationGlossary(27)}>cholesterol<strong>{res["cholesterol"]} {units["cholesterol"]}</strong>{riCholesterol}</a>
                </div>
            </React.Fragment>
        );
    };

    const renderPieCharts = (res, chartColours) => {
        let enProt = res["proteins"] * 4.18;
        let enCarb = res["carbohydrates"] * 4.18;
        let enFats = res["fats"] * 9;
        let enFibr = res["fiber"] * 2;
        let total = enProt + enCarb + enFats + enFibr;
        if (total === 0) total = 0.00001;
        enProt = Math.round((enProt / total) * 100);
        enCarb = Math.round((enCarb / total) * 100);
        enFats = Math.round((enFats / total) * 100);
        enFibr = 100 - enProt - enCarb - enFats;

        const chart1 = Util.pieChart([res["proteins"], res["carbohydrates"], res["fats"], res["fiber"]], 100, 100, 95, chartColours);
        const chart2 = Util.pieChart([enProt, enCarb, enFats, enFibr], 100, 100, 95, chartColours);

        return (
            <React.Fragment key="ratio_graphs">
                <h4 className={sectionHeader}>{__('Hmotnostní poměry živin')}</h4>
                <div className="px-1 py-1">
                    <svg viewBox="0 0 400 200" width="100%" height="100%">
                        {chart1}
                        <rect x="220" y="40" width="20" height="20" style={{ fill: chartColours[0], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="55" fill="#64748b">{__('Bílkoviny')} ({parseFloat(res["proteins"])} g)</text>
                        <rect x="220" y="70" width="20" height="20" style={{ fill: chartColours[1], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="85" fill="#64748b">{__('Sacharidy')} ({parseFloat(res["carbohydrates"])} g)</text>
                        <rect x="220" y="100" width="20" height="20" style={{ fill: chartColours[2], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="115" fill="#64748b">{__('Tuky')} ({parseFloat(res["fats"])} g)</text>
                        <rect x="220" y="130" width="20" height="20" style={{ fill: chartColours[3], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="145" fill="#64748b">{__('Vláknina')} ({parseFloat(res["fiber"])} g)</text>
                    </svg>
                </div>
                <h4 className={sectionHeader}>{__('Energetické poměry makroživin')}</h4>
                <div className="px-1 py-1">
                    <svg viewBox="0 0 400 200" width="100%" height="100%">
                        {chart2}
                        <rect x="220" y="40" width="20" height="20" style={{ fill: chartColours[0], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="55" fill="#64748b">Bílkoviny ({parseFloat(enProt)} %)</text>
                        <rect x="220" y="70" width="20" height="20" style={{ fill: chartColours[1], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="85" fill="#64748b">{__('Sacharidy')} ({parseFloat(enCarb)} %)</text>
                        <rect x="220" y="100" width="20" height="20" style={{ fill: chartColours[2], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="115" fill="#64748b">{__('Tuky')} ({parseFloat(enFats)} %)</text>
                        <rect x="220" y="130" width="20" height="20" style={{ fill: chartColours[3], strokeWidth: 0 }} />
                        <text fontSize="1.4em" x="250" y="145" fill="#64748b">{__('Vláknina')} ({parseFloat(enFibr)} %)</text>
                    </svg>
                </div>
            </React.Fragment>
        );
    };

    const renderCollapsedGroups = (res) => {
        const groups = [];
        for (let groupKey in globals.evaluation_groups_closed) {
            const subItems = [];
            for (let subGroup in globals.evaluation_groups[groupKey]) {
                const attributeKey = globals.evaluation_groups[groupKey][subGroup];
                const attributeUnit = globals.attribute_units[attributeKey];
                if (typeof res[attributeKey] === "undefined") continue;
                const glossaryId = typeof globals.glossary_ids?.[attributeKey] === 'number' ? globals.glossary_ids[attributeKey] : -1;

                let dddInfo = false;
                let riColor = "#64748b";
                if (ddd && typeof ddd[attributeKey] !== "undefined") {
                    dddInfo = ddd[attributeKey].percent + " % RI";
                    if (wholeDay === true && (ddd[attributeKey].overflow_direction === "min" || ddd[attributeKey].overflow_direction === "max")) {
                        riColor = "#ef4444";
                    }
                }

                subItems.push(
                    <div key={attributeKey} className={evalItemSingleCls}>
                        <a onClick={() => showEvaluationGlossary(glossaryId)}>
                            <span>{globals.attributes_short[attributeKey]}:</span>
                            <em style={{ color: riColor }}>{dddInfo}</em>
                            <strong>{res[attributeKey]} {attributeUnit}</strong>
                        </a>
                    </div>
                );

                if (attributeKey === 'added_sugars') {
                    const sugarCubes = Math.ceil((res[attributeKey] / 4) * 10) / 10;
                    subItems.push(
                        <div key="temp01" className={evalItemSingleCls}>
                            <a>
                                <span className="flex-[1_1_180px] mr-8">Odpovídá kostkám cukru:</span>
                                <strong>{sugarCubes}</strong>
                            </a>
                        </div>
                    );
                }
            }
            if (subItems.length > 0) {
                groups.push(
                    <div className={evalGroupCls} key={`subgroup_${groupKey}`}>
                        <input type="checkbox" id={"group-title-" + groupKey} />
                        <p>
                            <label htmlFor={"group-title-" + groupKey}>
                                <i className="fa fa-angle-right transition-transform duration-150"></i> {groupKey}
                            </label>
                        </p>
                        <div>{subItems}</div>
                    </div>
                );
            }
        }
        return groups.length > 0 ? <>{groups}</> : false;
    };

    const renderSimplifiedResults = (res) => {
        if (!wholeDay) return false;

        let recomReduce = [];
        let recomIncrease = [];
        let totalAttributes = 0;
        let percentDifference;

        if (res !== undefined) {
            const energyIntake = res["energy_kcal"] * 1;
            const energyLimitFrom = res["energy_balance_limit_from"] * 1;
            const energyLimitTo = res["energy_balance_limit_to"] * 1;
            if (energyIntake < energyLimitFrom) {
                const diff = Math.abs(energyLimitFrom - energyIntake);
                percentDifference = Math.ceil((diff / energyIntake) * 100);
                recomIncrease.push(<tr key="energy_intake_increase"><td>{__('Energetický příjem')}</td><td><span>{percentDifference} %</span></td></tr>);
            }
            if (energyIntake > energyLimitTo) {
                const diff = Math.abs(energyIntake - energyLimitTo);
                percentDifference = Math.ceil((diff / energyIntake) * 100);
                recomReduce.push(<tr key="energy_intake_decrease"><td>{__('Energetický příjem')}</td><td><span>{percentDifference} %</span></td></tr>);
            }
        }

        for (let k in ddd) {
            if (k === "alcohol") continue;
            const attrOrder = globals.attributes_order[k];
            if (ddd[k].overflow_direction !== "") {
                if (ddd[k].percent < 100) {
                    percentDifference = Math.ceil(((100 - ddd[k].percent) / 100) * 100);
                } else {
                    percentDifference = Math.ceil(((ddd[k].percent - 100) / ddd[k].percent) * 100);
                }
                const amountText = <span>{Math.ceil(ddd[k].amount_to_optimum)} {globals.attribute_units[k]}</span>;
                if (ddd[k].overflow_direction === "min") {
                    recomIncrease[attrOrder] = (<tr key="recom_increase"><td><a className="text-green-600 cursor-pointer hover:underline" onClick={(e) => showRecommendedFoods(e, k)}>{globals.attributes[k]}</a></td><td>{amountText}</td></tr>);
                }
                if (ddd[k].overflow_direction === "max") {
                    recomReduce[attrOrder] = (<tr key="recom_decrease"><td><a className="text-red-500 cursor-pointer hover:underline" onClick={(e) => showDecreaseFoods(e, k)}>{globals.attributes[k]}</a></td><td>{amountText}</td></tr>);
                }
            }
            totalAttributes++;
        }

        const reclen = Object.keys(recomReduce).length + Object.keys(recomIncrease).length;
        const percent = Math.round(((totalAttributes - reclen) / totalAttributes) * 100);
        let ratingText = "Je co zlepšovat...";
        let ratingColor = "text-amber-500";
        if (percent > 33) { ratingText = "Jste na dobré cestě!"; ratingColor = "text-blue-500"; }
        if (percent > 70) { ratingText = "Výborně!"; ratingColor = "text-green-500"; }

        const increaseEl = recomIncrease.length > 0 ? (
            <div className="mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <i className="fa fa-arrow-up text-green-500"></i> Doporučujeme navýšit:
                </p>
                <table className="w-full text-xs [&_td]:py-0.5 [&_td]:pr-1 [&_td:first-child]:flex-1 [&_td:last-child]:text-right">
                    <tbody>{Object.keys(recomIncrease).filter(e => e !== "0").map(e => recomIncrease[e])}</tbody>
                </table>
            </div>
        ) : false;

        const reduceEl = recomReduce.length > 0 ? (
            <div className="mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <i className="fa fa-arrow-down text-red-400"></i> Doporučujeme snížit:
                </p>
                <table className="w-full text-xs [&_td]:py-0.5 [&_td]:pr-1 [&_td:last-child]:text-right">
                    <tbody>{Object.keys(recomReduce).filter(e => e !== "0").map(e => recomReduce[e])}</tbody>
                </table>
            </div>
        ) : false;

        return (
            <div className="pt-4 px-2">
                <p className={`text-xl font-bold mb-1 ${ratingColor}`}>{ratingText}</p>
                <p className="text-xs text-slate-500 mb-4">Váš jídelníček dosahuje rating <b className="text-slate-700">{percent} %</b>.</p>
                {increaseEl}
                {reduceEl}
            </div>
        );
    };

    // -------------------------------------------------------------------------
    // Main render
    // -------------------------------------------------------------------------

    const units = globals.attribute_units;
    const evaluatingWholeDay = wholeDay === true;
    const chartColours = ["#96c999", "#eb714a", "#eae869", "#8bcbce", "#c694be"];

    if (results !== undefined) {
        for (let k in results) {
            let f = parseFloat(results[k]);
            if (k.indexOf("ratio") >= 0) {
                let fstr = results[k];
                if (typeof fstr === 'number') fstr = '' + fstr;
                if (fstr.indexOf(":") >= 0) { f = results[k]; continue; }
                f = f <= 1 ? "1 : " + (1 / f).toPrecision(2) : f.toPrecision(2) + " : 1";
            }
            if (typeof f === "number") f = Math.round(f * 100) / 100.0;
            results[k] = f;
        }
    }

    // Quick sidebar results
    let quickResultsItems = [];
    if (results !== undefined && quickResultsProp === true) {
        if (evaluatingWholeDay) {
            let balanceKcal = results["energy_balance_kcal"];
            let balanceKj = results["energy_balance_kj"];
            if (balanceKcal > 0) { balanceKcal = balanceKcal + ""; if (balanceKcal.substr(0, 1) !== "+") balanceKcal = "+" + balanceKcal; }
            if (balanceKj > 0) { balanceKj = balanceKj + ""; if (balanceKj.substr(0, 1) !== "+") balanceKj = "+" + balanceKj; }
            quickResultsItems.push(<React.Fragment key="balance"><p className={quickResultCls}><span>Bilance</span><strong>{balanceKcal} kcal</strong><strong>{balanceKj} kJ</strong></p></React.Fragment>);
        }
        quickResultsItems.push(
            <React.Fragment key="energy">
                <p className={quickResultCls}><span>kcal</span><strong>{results["energy_kcal"]}</strong></p>
                <p className={quickResultCls}><span>kJ</span><strong>{results["energy_kj"]}</strong></p>
            </React.Fragment>
        );
        let ta = results["total_amount"] || 0;
        let totalAmountInfo = ta + " g";
        if (ta >= 1000) { ta = Math.round((ta / 1000) * 1000) / 1000; totalAmountInfo = ta + " kg"; }
        quickResultsItems.push(
            <React.Fragment key="macros">
                <p className={quickResultCls}><span>Bílkoviny</span><strong>{results["proteins"]} g</strong></p>
                <p className={quickResultCls}><span>Sacharidy</span><strong>{results["carbohydrates"]} g</strong></p>
                <p className={quickResultCls}><span>Tuky</span><strong>{results["fats"]} g</strong></p>
                <p className={quickResultCls}><span>Vláknina</span><strong>{results["fiber"]} g</strong></p>
                <p className={quickResultCls}><span>Pitný režim</span><strong>{results["drink_amount"]} l</strong></p>
                <p className={quickResultCls}><span>Hmotnost</span><strong>{totalAmountInfo}</strong></p>
            </React.Fragment>
        );
    }

    const quickResultsEl = quickResultsItems.length > 0
        ? <div className="basis-[70px] shrink-0 rounded-tl border-r border-slate-100">{quickResultsItems}</div>
        : false;

    // Main results table
    let resultsTable = false;
    let resultsTableMacrosOnly = false;

    if (results !== undefined) {
        const macroSections = (
            <>
                {renderEnergySection(results, units, evaluatingWholeDay)}
                {renderMacrosBarChart(results, chartColours)}
                {renderMacrosSubItems(results, units)}
            </>
        );
        resultsTableMacrosOnly = macroSections;
        resultsTable = (
            <>
                {macroSections}
                {renderGlycemicSection(results)}
                {renderSaltCholesterolSection(results, units)}
                {renderPieCharts(results, chartColours)}
                {renderCollapsedGroups(results)}
            </>
        );
    }

    let simplifiedResults = renderSimplifiedResults(results);

    let numFoods = 0;
    Object.keys(data.foods).forEach(g => { numFoods += data.foods[g].length; });
    if (numFoods === 0) simplifiedResults = false;
    if (quickResultsProp === false) simplifiedResults = false;

    let toggleButtonText = "Skrýt detailní vyhodnocení";
    if (simplifiedResults !== false && !showAdvancedEvaluation) {
        resultsTable = false;
        toggleButtonText = "Zobrazit detailní vyhodnocení";
    }
    if (evaluationCollapsedAtStart === true && !showAdvancedEvaluation) {
        resultsTable = false;
        toggleButtonText = "Zobrazit detailní vyhodnocení";
    }

    const toggleAdvancedEvaluationButton = (wholeDay === true && !Util.isLectureLikeMode(data.appMode)) ? (
        <p className="mt-6 mb-4 text-center">
            <a className="inline-block py-2 px-4 text-xs font-medium bg-green-50 border border-green-300 text-green-700 rounded-lg no-underline hover:bg-green-100 cursor-pointer transition-colors" onClick={() => setShowAdvancedEvaluation(v => !v)}>
                {toggleButtonText}
            </a>
        </p>
    ) : false;

    if (macrosOnly === true) resultsTable = resultsTableMacrosOnly;

    const panelCls = `panel-evaluation grow-0 basis-full max-w-[300px] min-w-[300px]${extraClassName ? ' ' + extraClassName : ''}`;

    if (standalone) {
        return (
            <div className={`text-xs${extraClassName ? ' ' + extraClassName : ''}`} style={style || undefined}>
                {beforeContent}
                {simplifiedResults}
                {resultsTable}
                {toggleAdvancedEvaluationButton}
            </div>
        );
    }

    return (
        <div className={panelCls} style={style || undefined}>
            <div className="panel-evaluation-inner static">
                <div className="flex h-full">
                    {quickResultsEl}
                    <div className="flex flex-1 text-xs">
                        <div className="flex-1 pb-24 pl-1 pr-3 border-l border-slate-100">
                            {beforeContent}
                            {simplifiedResults}
                            {resultsTable}
                            {toggleAdvancedEvaluationButton}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EvaluationPanelContents;
